
import React, { useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import { AlertCircle, CheckCircle, ArrowRight } from 'lucide-react';
import { Container, Row, Col } from 'react-bootstrap';
import { useTranslation, Trans } from 'react-i18next';
import { markSeen } from '../utils/onboardingSeen';

const ActivationBanner = () => {
    const { user, business } = useContext(AuthContext);
    const { t } = useTranslation();
    const navigate = useNavigate();

    if (!user || !business) return null;

    // Collaborators (non-owners) should never see the activation banner
    // They are joining an existing business that is already configured
    if (user.role !== 'owner') {
        return null;
    }

    const isSetupComplete = user.is_setup_complete;
    const isSubscriptionActive = ['active', 'trialing'].includes(business.subscription_status);

    if (isSetupComplete && isSubscriptionActive) {
        return null;
    }

    // Calculate progress
    const stepsCompleted = (isSetupComplete ? 1 : 0) + (isSubscriptionActive ? 1 : 0);
    const progressPercentage = (stepsCompleted / 2) * 100;

    const bannerStyle = {
        background: 'linear-gradient(90deg, #4F46E5 0%, #3B82F6 100%)', // Indigo to Blue gradient
        color: 'white',
        borderBottom: '1px solid #4338ca',
        position: 'relative',
        zIndex: 1 // Low z-index to ensure dropdowns (z-index 1000) appear on top
    };

    const iconContainerStyle = {
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        padding: '0.5rem',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    };

    const stepButtonStyle = (completed) => ({
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.5rem 1rem',
        borderRadius: '0.5rem',
        textDecoration: 'none',
        transition: 'all 0.2s',
        border: '1px solid',
        backgroundColor: completed ? 'rgba(16, 185, 129, 0.2)' : 'white',
        borderColor: completed ? 'rgba(16, 185, 129, 0.3)' : 'white',
        color: completed ? '#d1fae5' : '#4338ca',
        cursor: completed ? 'default' : 'pointer',
        boxShadow: completed ? 'none' : '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
    });

    const stepCircleStyle = (completed) => ({
        width: '1.5rem',
        height: '1.5rem',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.75rem',
        fontWeight: 'bold',
        backgroundColor: completed ? '#10b981' : '#e0e7ff',
        color: completed ? 'white' : '#4338ca'
    });

    return (
        <div style={bannerStyle} className="shadow-lg">
            <Container className="py-3">
                <Row className="align-items-center gy-3">
                    {/* Left Side: Icon & Text */}
                    <Col md={7} lg={8} className="d-flex align-items-start align-items-md-center gap-3">
                        <div style={iconContainerStyle} className="flex-shrink-0">
                            <AlertCircle size={24} color="white" />
                        </div>
                        <div>
                            <h5 className="mb-1 fw-bold">
                                {t('activationBanner.title')}
                            </h5>
                            <p className="mb-0 text-white-50 small">
                                <Trans
                                    i18nKey="activationBanner.description"
                                    values={{
                                        mode: isSubscriptionActive && !isSetupComplete
                                            ? t('activationBanner.setupPendingMode', 'Configuración Incompleta')
                                            : t('activationBanner.readOnlyMode')
                                    }}
                                    components={{ modeWrapper: <span className="badge bg-white text-primary bg-opacity-25 text-uppercase mx-1" /> }}
                                />
                            </p>
                        </div>
                    </Col>

                    {/* Right Side: Steps & Actions */}
                    <Col md={5} lg={4}>
                        <div className="d-flex flex-column flex-sm-row gap-2 justify-content-md-end">
                            {/* Step 1: Business Data */}
                            <Link
                                id="onboarding-setup-business"
                                to="/settings/business"
                                style={stepButtonStyle(isSetupComplete)}
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (isSetupComplete) return;
                                    // El spotlight global ya cumplió su propósito (llevar al
                                    // usuario aquí) — se marca visto para que no siga compitiendo
                                    // con la página de destino, donde este mismo botón también
                                    // está presente (el banner se monta en todas las rutas).
                                    markSeen(user.id, 'setup_business');
                                    navigate('/settings/business', { state: { scrollToSave: true } });
                                }}
                                className="text-decoration-none"
                            >
                                <div style={stepCircleStyle(isSetupComplete)}>
                                    {isSetupComplete ? <CheckCircle size={14} /> : '1'}
                                </div>
                                <span className="small fw-bold">{t('activationBanner.businessDataStep')}</span>
                                {!isSetupComplete && <ArrowRight size={16} className="ms-auto" />}
                            </Link>

                            {/* Step 2: Subscription */}
                            <Link
                                id="onboarding-subscribe"
                                to="/settings/plan"
                                style={stepButtonStyle(isSubscriptionActive)}
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (isSubscriptionActive) return;
                                    // Mismo criterio que "Datos del Negocio": apaga el spotlight
                                    // global de este paso (también presente en la página de
                                    // destino) y navega con la señal para hacer scroll hasta
                                    // los planes.
                                    markSeen(user.id, 'subscribe');
                                    navigate('/settings/plan', { state: { scrollToPlans: true } });
                                }}
                                className="text-decoration-none"
                            >
                                <div style={stepCircleStyle(isSubscriptionActive)}>
                                    {isSubscriptionActive ? <CheckCircle size={14} /> : '2'}
                                </div>
                                <span className="small fw-bold">{t('activationBanner.subscriptionStep')}</span>
                                {!isSubscriptionActive && <ArrowRight size={16} className="ms-auto" />}
                            </Link>
                        </div>
                    </Col>
                </Row>
            </Container>

            {/* Progress Bar */}
            <div style={{
                height: '4px',
                width: '100%',
                backgroundColor: 'rgba(0,0,0,0.1)',
                position: 'absolute',
                bottom: 0,
                left: 0
            }}>
                <div style={{
                    height: '100%',
                    width: `${progressPercentage}% `,
                    backgroundColor: '#34d399',
                    transition: 'width 1s ease-out'
                }} />
            </div>
        </div>
    );
};

export default ActivationBanner;

